import { TFunction } from 'i18next'

import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'

import { TransactionsStaticActionItem } from '../../../types/transactionsStaticActionItem'
import { getBillsActions } from '../elements/TransactionsTableStaticActions/utils/getBillsActions'
import { getDaybooksActions } from '../elements/TransactionsTableStaticActions/utils/getDaybooksActions'
import { getInvoicesActions } from '../elements/TransactionsTableStaticActions/utils/getInvoicesActions'
import { getSalesTaxPaymentsActions } from '../elements/TransactionsTableStaticActions/utils/getSalesTaxPaymentsActions'
import { getSalesTaxReturnsActions } from '../elements/TransactionsTableStaticActions/utils/getSalesTaxReturnsActions'
import { getPaymentsActions } from '../elements/TransactionsTableStaticActions/utils/getsPaymentsActions'

export enum TransactionsCommonStaticActionType {
  CopyLink = 'copyLink',
}

interface GetBillsTableStaticActionsProps {
  daybookId?: string
  isBillPaid?: boolean
  isInvoicePaid?: boolean
  isVATPeriodSettled?: boolean
  isVATPeriodPaid?: boolean
  isVoided?: boolean
  transactionType?: TransactionOriginatorType
  t: TFunction
}

const getCopyLinkAction = (t: TFunction): TransactionsStaticActionItem => ({
  children: t('transactions.table.static_actions.copy_link'),
  id: TransactionsCommonStaticActionType.CopyLink,
  value: TransactionsCommonStaticActionType.CopyLink,
})

// Daybook transactions are exempt: unlike other types, copy_to_daybook must stay available even
// once voided, so they skip the blanket isVoided-hides-everything rule and gate themselves instead.
const getTypeSpecificActions = ({
  daybookId,
  isBillPaid,
  isInvoicePaid,
  isVATPeriodSettled,
  isVATPeriodPaid,
  isVoided,
  transactionType,
  t,
}: GetBillsTableStaticActionsProps) => {
  if (isVoided && transactionType !== TransactionOriginatorType.DaybookTransaction) {
    return []
  }

  switch (transactionType) {
    case TransactionOriginatorType.Bill:
      return getBillsActions({ isBillPaid, t })
    case TransactionOriginatorType.Invoice:
      return getInvoicesActions({ isInvoicePaid, t })
    case TransactionOriginatorType.BankPayment:
      return getPaymentsActions({ t })
    case TransactionOriginatorType.SalesTaxPayment:
      return getSalesTaxPaymentsActions({ t })
    case TransactionOriginatorType.SalesTaxReturn:
      return getSalesTaxReturnsActions({ isVATPeriodSettled, isVATPeriodPaid, t })
    case TransactionOriginatorType.DaybookTransaction:
      return getDaybooksActions({ hasDaybookReference: !!daybookId, isVoided, t })
    default:
      return []
  }
}

export const getTransactionsStaticActions = ({
  daybookId,
  isBillPaid,
  isInvoicePaid,
  isVATPeriodSettled,
  isVATPeriodPaid,
  isVoided,
  transactionType,
  t,
}: GetBillsTableStaticActionsProps) => {
  const typeSpecificActions = getTypeSpecificActions({
    daybookId,
    isBillPaid,
    isInvoicePaid,
    isVATPeriodSettled,
    isVATPeriodPaid,
    isVoided,
    transactionType,
    t,
  })

  return [...typeSpecificActions, getCopyLinkAction(t)]
}
